'use client';

import { c as _c } from "react/compiler-runtime";
import { useFeatureFlags } from '@repo/feature-flags/react/feature-flags-provider';
import type { WandAnalyticsParams } from '@repo/wemod-api-client/analytics/analytics-cookie';
import { type CSSProperties, type ReactNode, useEffect } from 'react';
import { useAdAnalytics } from '../hooks/use-ad-analytics';
import { useClientAdPreviewMode } from '../hooks/use-client-ad-preview-mode';
import { useEnsureLiverampReady } from '../hooks/use-ensure-liveramp-ready';
import { useWandAnalyticsParams } from '../hooks/use-wand-analytics-params';
import type { AdProviderSlotProps, AdProviders, AdSlotProps } from '../types/ad-slot-props';
import { logAdLifecycle } from '../utils/ad-lifecycle-log';
import { shouldShowAdSlot } from '../utils/eligibility';
import { AditudeSlot } from './providers/aditude/aditude-slot';
type AdSlotClientProps = AdSlotProps & {
  analyticsParams: WandAnalyticsParams | null;
  forceShowAd: boolean;
  isServerRendered?: boolean;
};
type ProviderSlotProps = {
  provider: AdProviders;
} & AdProviderSlotProps;
function renderProviderSlot({
  provider,
  ...slotProps
}: ProviderSlotProps): ReactNode {
  switch (provider) {
    case 'aditude-maps-wand':
    case 'aditude-games-wand':
      return <AditudeSlot {...slotProps} />;
    default:
      {
        const _unhandledProvider: never = provider;
        return _unhandledProvider;
      }
  }
}
const adSlotTargetStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  position: 'relative'
};
function registerLiverampHem(emailHash: string, divId: string): void {
  const apply = (): void => {
    window.atsenvelopemodule?.setAdditionalData?.({
      type: 'emailHashes',
      id: [emailHash]
    });
    logAdLifecycle('liveramp:hem', {
      divId
    });
  };
  if (window.atsenvelopemodule) {
    apply();
    return;
  }
  logAdLifecycle('liveramp:hem', {
    divId,
    waitingFor: 'envelopeModuleReady'
  });
  window.addEventListener('envelopeModuleReady', apply, {
    once: true
  });
}
export function AdSlotClient(t0) {
  const $ = _c(35);
  const {
    provider,
    slotId,
    divId,
    analyticsLocation,
    metadata,
    className,
    style,
    eligibilityKey,
    children,
    analyticsParams,
    forceShowAd,
    isServerRendered: t1
  } = t0;
  const isServerRendered = t1 === undefined ? false : t1;
  const flags = useFeatureFlags();
  const params = useWandAnalyticsParams(analyticsParams, eligibilityKey);
  const clientPreviewMode = useClientAdPreviewMode();
  const t2 = forceShowAd || clientPreviewMode;
  let t3;
  if ($[0] !== flags || $[1] !== params || $[2] !== t2) {
    t3 = shouldShowAdSlot(params, flags, t2);
    $[0] = flags;
    $[1] = params;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const shouldShowAd = t3;
  const {
    isReady
  } = useEnsureLiverampReady(shouldShowAd, isServerRendered);
  const {
    onAdStart,
    cancelAdStart,
    trackSlotRendered,
    trackInitialized,
    trackEmpty,
    startFillTimeouts,
    clearFillTimeouts
  } = useAdAnalytics(analyticsLocation, provider);
  const emailHash = params?.email?.sha256 ?? null;
  let t4;
  let t5;
  if ($[4] !== shouldShowAd || $[5] !== trackSlotRendered) {
    t4 = () => {
      if (!shouldShowAd) {
        return;
      }
      trackSlotRendered();
    };
    t5 = [shouldShowAd, trackSlotRendered];
    $[4] = shouldShowAd;
    $[5] = trackSlotRendered;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[8] !== divId || $[9] !== emailHash || $[10] !== isReady) {
    t6 = () => {
      if (!isReady || !emailHash) {
        return;
      }
      registerLiverampHem(emailHash, divId);
    };
    t7 = [divId, emailHash, isReady];
    $[8] = divId;
    $[9] = emailHash;
    $[10] = isReady;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  if (!shouldShowAd) {
    return null;
  }
  let t8;
  if ($[13] !== cancelAdStart || $[14] !== clearFillTimeouts || $[15] !== divId || $[16] !== emailHash || $[17] !== isReady || $[18] !== isServerRendered || $[19] !== metadata || $[20] !== onAdStart || $[21] !== provider || $[22] !== slotId || $[23] !== startFillTimeouts || $[24] !== trackEmpty || $[25] !== trackInitialized) {
    t8 = renderProviderSlot({
      provider,
      slotId,
      divId,
      metadata,
      emailHash,
      isReady,
      isServerRendered,
      onAdStart,
      cancelAdStart,
      trackInitialized,
      trackEmpty,
      startFillTimeouts,
      clearFillTimeouts
    });
    $[13] = cancelAdStart;
    $[14] = clearFillTimeouts;
    $[15] = divId;
    $[16] = emailHash;
    $[17] = isReady;
    $[18] = isServerRendered;
    $[19] = metadata;
    $[20] = onAdStart;
    $[21] = provider;
    $[22] = slotId;
    $[23] = startFillTimeouts;
    $[24] = trackEmpty;
    $[25] = trackInitialized;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== divId || $[28] !== t8) {
    t9 = <div id={divId} style={adSlotTargetStyle}>{t8}</div>;
    $[27] = divId;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== children || $[31] !== className || $[32] !== style || $[33] !== t9) {
    t10 = <div className={className} style={style}>{t9}{children}</div>;
    $[30] = children;
    $[31] = className;
    $[32] = style;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  return t10;
}